import { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItemButton,
  ListItemText,
  CircularProgress,
  Box,
  Typography,
} from "@mui/material";
import { fetchAvailableClassesForWeek } from "../api/classesApi";

function AddExistingClassDialog({
  open,
  onClose,
  onAdd,
  campusName,
  weekLabel,
  calendarWeekId,
  scheduling,
}) {
  const [available, setAvailable] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pendingId, setPendingId] = useState(null);

  useEffect(() => {
    if (!open || !calendarWeekId) return;

    let cancelled = false;
    setLoading(true);
    fetchAvailableClassesForWeek(calendarWeekId)
      .then((data) => {
        if (cancelled) return;
        setAvailable(Array.isArray(data) ? data : []);
      })
      .catch((err) => {
        console.error("Failed to load available classes", err);
        if (!cancelled) setAvailable([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, calendarWeekId]);

  const handleAdd = async (cls) => {
    setPendingId(cls.id);
    try {
      await onAdd(cls);
      setAvailable((prev) => prev.filter((c) => c.id !== cls.id));
    } finally {
      setPendingId(null);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        Add existing class — {campusName} week {weekLabel}
      </DialogTitle>
      <DialogContent dividers>
        {loading ? (
          <Box py={4} display="flex" justifyContent="center">
            <CircularProgress size={28} />
          </Box>
        ) : available.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: "center" }}>
            No other classes available for this campus.
          </Typography>
        ) : (
          <List dense>
            {available.map((cls) => (
              <ListItemButton
                key={cls.id}
                onClick={() => handleAdd(cls)}
                disabled={scheduling || pendingId === cls.id}
              >
                <ListItemText
                  primary={cls.groupName}
                  secondary={cls.leaderName ? `Teacher: ${cls.leaderName}` : "No teacher assigned"}
                />
                {pendingId === cls.id && <CircularProgress size={18} />}
              </ListItemButton>
            ))}
          </List>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

export default AddExistingClassDialog;
